import React from 'react'
import { makeStyles } from '@mui/styles'
import clsx from 'clsx'
import lang from '@/lang'
import ThemeInterface from '@/theme-interface'

const useStyles = makeStyles((theme: ThemeInterface) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    width: '50%',
    minWidth: 200,
    maxWidth: 660,
    height: '100%',
    padding: '34px 32px 48px',
    boxSizing: 'border-box',
    overflowY: 'auto',
    borderLeft: `2px solid ${theme.borderColor}`,
    background: theme.backgroundColorLight,
  },
  fullWidth: {
    width: '100%',
    maxWidth: 'none',
    borderLeft: 0,
  },
  title: {
    marginBottom: 10,
    fontSize: theme.fontSizes.medium,
    fontWeight: theme.fontWeights.medium,
    color: theme.primary,
  },
  progressTrack: {
    position: 'relative',
    flex: '0 0 auto',
    width: '100%',
    height: 8,
    marginBottom: 28,
    overflow: 'hidden',
    borderRadius: 4,
    background: theme.backgroundColor,
  },
  // @keyframes is not a class; the plugin cannot see it in the animation shorthand below.
  // eslint-disable-next-line mui-unused-classes/unused-classes
  '@keyframes invoiceAnalysisPlaceholderProgress': {
    '0%': { transform: 'translateX(-140%)' },
    '100%': { transform: 'translateX(470%)' },
  },
  progressValue: {
    display: 'block',
    width: '28%',
    height: '100%',
    borderRadius: 'inherit',
    background: theme.primary,
    animation: '$invoiceAnalysisPlaceholderProgress 1.55s ease-in-out infinite',
  },
  fields: {
    display: 'flex',
    flexDirection: 'column',
    gap: 25,
  },
  field: {
    display: 'flex',
    flex: 1,
    minWidth: 0,
    flexDirection: 'column',
    gap: 8,
  },
  fieldRow: {
    display: 'flex',
    gap: 24,
  },
  block: {
    display: 'block',
    borderRadius: 5,
    background: `linear-gradient(90deg, ${theme.backgroundColor} 25%, ${theme.backgroundLight} 50%, ${theme.backgroundColor} 75%)`,
    backgroundSize: '220% 100%',
    animation: '$invoiceAnalysisPlaceholderShimmer 1.8s ease-in-out infinite',
  },
  // eslint-disable-next-line mui-unused-classes/unused-classes
  '@keyframes invoiceAnalysisPlaceholderShimmer': {
    '0%': { backgroundPosition: '200% 0' },
    '100%': { backgroundPosition: '-20% 0' },
  },
  labelBlock: {
    height: 20,
  },
  inputBlock: {
    width: '100%',
    height: 42,
  },
  textAreaBlock: {
    height: 56,
  },
  divider: {
    height: 1,
    margin: '0 0 -2px',
    background: theme.borderColor,
  },
  // Media queries are not classes; the plugin reports their computed key as `undefined`.
  // eslint-disable-next-line mui-unused-classes/unused-classes
  '@media (prefers-reduced-motion: reduce)': {
    progressValue: {
      animation: 'none',
      transform: 'translateX(128%)',
    },
    block: {
      animation: 'none',
      background: theme.backgroundColor,
    },
  },
}))

interface PlaceholderFieldProps {
  labelWidth?: string
  multiline?: boolean
}

const PlaceholderField = ({ labelWidth = '22%', multiline = false }: PlaceholderFieldProps) => {
  const classes = useStyles()

  return (
    <div className={classes.field} data-testid='invoice-analysis-placeholder-field' aria-hidden='true'>
      <span className={clsx(classes.block, classes.labelBlock)} style={{ width: labelWidth }} />
      <span className={clsx(classes.block, multiline ? classes.textAreaBlock : classes.inputBlock)} />
    </div>
  )
}

interface InvoiceAnalysisPlaceholderProps {
  fullWidth?: boolean
}

// FLAG:useDocuAi — shown only after a receipt was uploaded from a new expense/revenue and until
// DocuAI produced the review data. It previews the shape of the review component without presenting
// editable defaults that could be mistaken for analysed values.
const InvoiceAnalysisPlaceholder = ({ fullWidth = false }: InvoiceAnalysisPlaceholderProps) => {
  const classes = useStyles()
  const title = lang.receipt.form.analysisPlaceholder

  return (
    <div
      className={clsx(classes.root, fullWidth && classes.fullWidth)}
      data-testid='invoice-analysis-placeholder'
      role='status'
      aria-live='polite'
      aria-label={title}
    >
      <span className={classes.title}>{title}</span>
      <div className={classes.progressTrack} role='progressbar' aria-label={title} aria-valuetext={title}>
        <span className={classes.progressValue} />
      </div>
      <div className={classes.fields} aria-hidden='true'>
        <PlaceholderField />
        <PlaceholderField />
        <div className={classes.fieldRow}>
          <PlaceholderField labelWidth='28%' />
          <PlaceholderField labelWidth='28%' />
        </div>
        <div className={classes.divider} />
        <PlaceholderField multiline />
        <div className={classes.divider} />
        <PlaceholderField />
        <PlaceholderField multiline />
      </div>
    </div>
  )
}

export default InvoiceAnalysisPlaceholder
